import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { documentService } from '../services/documentService';
import { Upload, Download, Trash2, File, Folder, ChevronRight, Home, X, Edit2, Search, FileText, Image, Archive, Lock, Shield, Eye, Mail, Cpu, Building2, Share2, Clock, PenTool, Plus } from 'lucide-react';
import { sendDocumentForSignature, sendSignatureRequest } from '../services/esignatureService';
import SavedEmails from '../components/documents/SavedEmails';
import ScanProcessor from '../components/documents/ScanProcessor';
import { ProtectionIndicator } from '../components/documents/ProtectionBadges';
import ManageProtectionsModal from '../components/documents/ManageProtectionsModal';
import ShareDocumentModal from '../components/documents/ShareDocumentModal';
import DocumentApprovalQueue from '../components/documents/DocumentApprovalQueue';
import SpaceBackground from '../components/layout/SpaceBackground';
import api from '../services/api';

const Documents = () => {
  const { user, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('cases');
  const [organized, setOrganized] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [caseFolders, setCaseFolders] = useState([]);
  const [caseFolderId, setCaseFolderId] = useState(null);
  const [unfiledDocumentCount, setUnfiledDocumentCount] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [protectionsDoc, setProtectionsDoc] = useState(null);
  const [shareDoc, setShareDoc] = useState(null);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);

  // Signature modal state
  const [signatureDoc, setSignatureDoc] = useState(null);
  const [signatureSigners, setSignatureSigners] = useState([{ name: '', email: '', type: 'client' }]);
  const [sendingForSignature, setSendingForSignature] = useState(false);

  // Check if user is attorney or admin (can approve shares)
  const canApproveShares = user?.role === 'attorney' || user?.role === 'admin';

  useEffect(() => {
    loadOrganized();
    if (canApproveShares) {
      loadPendingApprovalCount();
    }
  }, []);

  const loadPendingApprovalCount = async () => {
    try {
      const response = await api.get('/documents/shares/pending');
      setPendingApprovalCount(response.data?.length || 0);
    } catch (err) {
      console.error('Error loading pending approval count:', err);
    }
  };

  useEffect(() => {
    if (organized) {
      loadDocuments();
    }
  }, [activeTab, selectedClient, selectedCase, selectedFolder, organized]);

  const loadOrganized = async () => {
    try {
      const data = await documentService.getOrganized();
      setOrganized(data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load organized structure:', err);
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    setLoading(true);
    setSearchResults(null);
    try {
      let data;
      if (activeTab === 'cases') {
        if (selectedCase && selectedFolder) {
          // Load documents for specific folder within a case
          // If folder_type is 'case' (All Documents view), get all case documents directly
          if (selectedFolder.folder_type === 'case' || !selectedFolder.id) {
            // "All Documents" view - get all documents for the case
            data = await documentService.getCaseDocuments(selectedCase.id);
            setDocuments(data.documents || []);
            setCaseFolders([]); // Don't show subfolders in All Documents view
          } else {
            // Regular folder - get folder contents
            data = await documentService.getFolderContents(selectedFolder.id);
            setDocuments(data.documents || []);
            setCaseFolders(data.subfolders || []);
          }
        } else if (selectedCase) {
          // Load case folders (subfolders)
          data = await documentService.getCaseFolders(selectedCase.id);
          setCaseFolders(data.folders || []);
          setCaseFolderId(data.case_folder_id);
          setUnfiledDocumentCount(data.unfiled_document_count || 0);
          setDocuments([]);
        } else {
          // Show clients or cases list (no documents at root levels)
          setDocuments([]);
          setCaseFolders([]);
        }
      } else if (activeTab === 'templates') {
        data = await documentService.listDocuments({ scope: 'template' });
        setDocuments(data.documents || []);
        setCaseFolders([]);
      } else if (activeTab === 'scans') {
        data = await documentService.listDocuments({ scope: 'scans' });
        setDocuments(data.documents || []);
        setCaseFolders([]);
      } else if (activeTab === 'personal') {
        data = await documentService.listDocuments({ scope: 'personal' });
        setDocuments(data.documents || []);
        setCaseFolders([]);
      } else if (activeTab === 'business') {
        data = await documentService.listDocuments({ scope: 'business' });
        setDocuments(data.documents || []);
        setCaseFolders([]);
      }
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const scope = activeTab === 'cases' ? 'case' : activeTab === 'templates' ? 'template' : activeTab === 'scans' ? 'scans' : activeTab === 'business' ? 'business' : 'personal';
      const data = await documentService.searchDocuments(searchQuery, scope);
      setSearchResults(data.documents || []);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearching(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedClient(null);
    setSelectedCase(null);
    setSelectedFolder(null);
    setCaseFolders([]);
    setSearchQuery('');
    setSearchResults(null);
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setSelectedCase(null);
    setSelectedFolder(null);
    setCaseFolders([]);
    setSearchQuery('');
    setSearchResults(null);
  };

  const handleCaseSelect = (caseItem) => {
    setSelectedCase(caseItem);
    setSelectedFolder(null);
    setSearchQuery('');
    setSearchResults(null);
  };

  const handleFolderSelect = (folder) => {
    setSelectedFolder(folder);
    setSearchQuery('');
    setSearchResults(null);
  };

  const handleBackToClients = () => {
    setSelectedClient(null);
    setSelectedCase(null);
    setSelectedFolder(null);
    setCaseFolders([]);
  };

  const handleBackToCases = () => {
    setSelectedCase(null);
    setSelectedFolder(null);
    setCaseFolders([]);
  };

  const handleBackToFolders = () => {
    setSelectedFolder(null);
  };

  const handleDownload = async (doc) => {
    try {
      await documentService.downloadDocument(doc.id, (doc.display_name || doc.original_file_name));
    } catch (err) {
      console.error('Download failed:', err);
      alert('Download failed. Please try again.');
    }
  };

  const handleRename = async (doc) => {
    const newName = prompt(`Rename "${(doc.display_name || doc.original_file_name)}" to:`, (doc.display_name || doc.original_file_name));
    if (!newName || newName === (doc.display_name || doc.original_file_name)) return;

    try {
      await documentService.renameDocument(doc.id, newName);
      loadDocuments();
    } catch (err) {
      console.error('Rename failed:', err);
      alert('Rename failed. Please try again.');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${(doc.display_name || doc.original_file_name)}"?`)) return;

    try {
      await documentService.deleteDocument(doc.id);
      loadDocuments();
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Delete failed. Please try again.');
    }
  };

  // Signature handlers
  const handleSendForSignature = (doc) => {
    setSignatureDoc(doc);
    setSignatureSigners([{ name: '', email: '', type: 'client' }]);
  };

  const handleAddSignatureSigner = () => {
    setSignatureSigners([...signatureSigners, { name: '', email: '', type: 'client' }]);
  };

  const handleRemoveSignatureSigner = (index) => {
    if (signatureSigners.length > 1) {
      setSignatureSigners(signatureSigners.filter((_, i) => i !== index));
    }
  };

  const handleSignatureSignerChange = (index, field, value) => {
    const updated = [...signatureSigners];
    updated[index][field] = value;
    setSignatureSigners(updated);
  };

  const handleSubmitForSignature = async () => {
    const validSigners = signatureSigners.filter(s => s.name.trim() && s.email.trim());
    if (validSigners.length === 0) {
      alert('Please add at least one signer with name and email');
      return;
    }

    try {
      setSendingForSignature(true);
      // Step 1: Create the signature request
      const response = await sendDocumentForSignature({
        document_id: signatureDoc.id,
        document_type: 'virgo_document',
        signers: validSigners.map((s, idx) => ({
          signer_name: s.name,
          signer_email: s.email,
          signer_type: s.type,
          signing_order: idx + 1
        })),
        case_id: selectedCase?.id
      });

      // Step 2: Send the signature request (emails signers)
      const requestId = response.data.id;
      await sendSignatureRequest(requestId);

      alert('Document sent for signature! The signer(s) will receive an email shortly.');
      setSignatureDoc(null);
    } catch (err) {
      console.error('Failed to send for signature:', err);
      alert(err.response?.data?.detail || 'Failed to send document for signature');
    } finally {
      setSendingForSignature(false);
    }
  };

  const handleUploadClick = () => {
    // For cases tab, require a case to be selected
    if (activeTab === 'cases' && !selectedCase) {
      alert('Please select a case first');
      return;
    }
    setShowUploadModal(true);
    setSelectedFile(null);
    setUploadError(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError('Please select a file');
      return;
    }

    const scope = activeTab === 'cases' ? 'case' : activeTab === 'templates' ? 'template' : activeTab === 'scans' ? 'scans' : activeTab === 'business' ? 'business' : 'personal';
    const options = {};
    if (scope === 'case' && selectedCase) {
      options.caseId = selectedCase.id;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 100);

      await documentService.uploadDocument(selectedFile, scope, options);

      clearInterval(progressInterval);
      setUploadProgress(100);

      setTimeout(() => {
        setShowUploadModal(false);
        setSelectedFile(null);
        setUploading(false);
        setUploadProgress(0);
        loadDocuments();
      }, 500);
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadError(err.response?.data?.detail || 'Upload failed. Please try again.');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const closeUploadModal = () => {
    if (!uploading) {
      setShowUploadModal(false);
      setSelectedFile(null);
      setUploadError(null);
    }
  };

  const getFileIcon = (mimeType) => {
    const iconType = documentService.getFileIcon(mimeType);
    const iconProps = { size: 20, className: 'mr-3' };

    switch (iconType) {
      case 'image':
        return <Image {...iconProps} className="text-green-400 mr-3" />;
      case 'pdf':
        return <FileText {...iconProps} className="text-red-400 mr-3" />;
      case 'archive':
        return <Archive {...iconProps} className="text-yellow-400 mr-3" />;
      default:
        return <File {...iconProps} className="text-virgo-teal-500 mr-3" />;
    }
  };

  const canUpload = () => {
    if (activeTab === 'cases') return !!selectedCase && user?.role !== 'attorney';
    if (activeTab === 'templates') return user?.role === 'staff' || user?.role === 'admin' || user?.role === 'paralegal';
    if (activeTab === 'scans') return user?.role === 'staff' || user?.role === 'admin' || user?.role === 'paralegal';
    if (activeTab === 'personal') return true;
    if (activeTab === 'business') return user?.role === 'admin';
    return false;
  };

  const canDelete = (doc) => {
    if (doc.document_scope === 'personal') return true; // Owner only, enforced by backend
    return user?.role === 'staff' || user?.role === 'admin' || user?.role === 'paralegal';
  };

  if (loading && !organized) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  const displayDocuments = searchResults !== null ? searchResults : documents;

  return (
    <div className="max-w-7xl mx-auto relative">
      <SpaceBackground />
      <div className="relative z-10">
      {/* Header with Search and Upload */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-virgo-teal-100">Documents</h1>
        <div className="flex items-center space-x-3">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search documents..."
              className="pl-10 pr-4 py-2 bg-virgo-navy-600 border border-virgo-teal-600 rounded-lg text-white placeholder-virgo-teal-500 w-64"
            />
            <Search
              size={18}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-virgo-teal-500 cursor-pointer"
              onClick={handleSearch}
            />
          </div>
          {canUpload() && (
            <button
              onClick={handleUploadClick}
              className="flex items-center px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-500"
            >
              <Upload size={18} className="mr-2" />
              Upload
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 mb-6 border-b border-virgo-teal-700">
        <button
          onClick={() => handleTabChange('cases')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'cases'
              ? 'text-virgo-teal-400 border-b-2 border-virgo-teal-400'
              : 'text-virgo-teal-600 hover:text-virgo-teal-300'
          }`}
        >
          Cases ({organized?.clients?.length || 0} clients)
        </button>
        <button
          onClick={() => handleTabChange('templates')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'templates'
              ? 'text-virgo-teal-400 border-b-2 border-virgo-teal-400'
              : 'text-virgo-teal-600 hover:text-virgo-teal-300'
          }`}
        >
          Templates ({organized?.templates?.document_count || 0})
        </button>
        <button
          onClick={() => handleTabChange('scans')}
          className={`px-4 py-2 font-medium flex items-center ${
            activeTab === 'scans'
              ? 'text-virgo-teal-400 border-b-2 border-virgo-teal-400'
              : 'text-virgo-teal-600 hover:text-virgo-teal-300'
          }`}
        >
          <Cpu size={16} className="mr-1.5" />
          Scan Processor
        </button>
        <button
          onClick={() => handleTabChange('personal')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'personal'
              ? 'text-virgo-teal-400 border-b-2 border-virgo-teal-400'
              : 'text-virgo-teal-600 hover:text-virgo-teal-300'
          }`}
        >
          Personal ({organized?.personal?.document_count || 0})
        </button>
        {isAdmin && organized?.business && (
          <button
            onClick={() => handleTabChange('business')}
            className={`px-4 py-2 font-medium flex items-center ${
              activeTab === 'business'
                ? 'text-virgo-gold-400 border-b-2 border-virgo-gold-400'
                : 'text-virgo-gold-600 hover:text-virgo-gold-300'
            }`}
          >
            <Building2 size={16} className="mr-1.5" />
            Business ({organized?.business?.document_count || 0})
          </button>
        )}
        <button
          onClick={() => handleTabChange('saved-emails')}
          className={`px-4 py-2 font-medium flex items-center ${
            activeTab === 'saved-emails'
              ? 'text-virgo-teal-400 border-b-2 border-virgo-teal-400'
              : 'text-virgo-teal-600 hover:text-virgo-teal-300'
          }`}
        >
          <Mail size={16} className="mr-1.5" />
          Saved Emails
        </button>
        {canApproveShares && (
          <button
            onClick={() => handleTabChange('approvals')}
            className={`px-4 py-2 font-medium flex items-center ${
              activeTab === 'approvals'
                ? 'text-amber-400 border-b-2 border-amber-400'
                : 'text-amber-600 hover:text-amber-300'
            }`}
          >
            <Clock size={16} className="mr-1.5" />
            Approvals
            {pendingApprovalCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs">
                {pendingApprovalCount}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Breadcrumb */}
      {activeTab === 'cases' && (selectedClient || selectedCase || selectedFolder) && (
        <div className="mb-4 flex items-center space-x-2 text-sm">
          <button
            onClick={handleBackToClients}
            className="flex items-center text-virgo-teal-400 hover:text-virgo-teal-200"
          >
            <Home size={16} className="mr-1" />
            <span>Cases</span>
          </button>
          {selectedClient && (
            <>
              <ChevronRight size={16} className="text-virgo-teal-500" />
              {selectedCase ? (
                <button
                  onClick={handleBackToCases}
                  className="text-virgo-teal-400 hover:text-virgo-teal-200"
                >
                  {selectedClient.name}
                </button>
              ) : (
                <span className="text-white">{selectedClient.name}</span>
              )}
            </>
          )}
          {selectedCase && (
            <>
              <ChevronRight size={16} className="text-virgo-teal-500" />
              {selectedFolder ? (
                <button
                  onClick={handleBackToFolders}
                  className="text-virgo-teal-400 hover:text-virgo-teal-200"
                >
                  {selectedCase.case_name}
                </button>
              ) : (
                <span className="text-white">{selectedCase.case_name}</span>
              )}
            </>
          )}
          {selectedFolder && (
            <>
              <ChevronRight size={16} className="text-virgo-teal-500" />
              <span className="text-white">{selectedFolder.name}</span>
            </>
          )}
        </div>
      )}

      {/* Saved Emails Tab */}
      {activeTab === 'saved-emails' && (
        <SavedEmails />
      )}

      {/* Scans Tab - AI Processing */}
      {activeTab === 'scans' && (
        <ScanProcessor onDocumentFiled={loadDocuments} />
      )}

      {/* Approvals Tab */}
      {activeTab === 'approvals' && canApproveShares && (
        <div className="bg-virgo-navy-700 rounded-lg shadow p-6">
          <DocumentApprovalQueue
            onApprovalChange={loadPendingApprovalCount}
          />
        </div>
      )}

      {/* Content Area */}
      {activeTab !== 'saved-emails' && activeTab !== 'scans' && activeTab !== 'approvals' && (
      <div className="bg-virgo-navy-700 rounded-lg shadow">
        {loading ? (
          <div className="p-8 text-center text-virgo-teal-300">Loading...</div>
        ) : searching ? (
          <div className="p-8 text-center text-virgo-teal-300">Searching...</div>
        ) : (
          <div className="overflow-x-auto">
            {/* Clients list view (top level of Cases) */}
            {activeTab === 'cases' && !selectedClient && !selectedCase && searchResults === null && (
              <table className="w-full">
                <thead className="bg-virgo-navy-600 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Cases</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Documents</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {organized?.clients?.map((client) => (
                    <tr
                      key={client.id}
                      className="hover:bg-virgo-navy-600 cursor-pointer"
                      onClick={() => handleClientSelect(client)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Folder className="text-yellow-400 mr-3" size={20} />
                          <span className="text-white font-medium">{client.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-virgo-teal-400">
                        {client.cases?.length || 0} case{(client.cases?.length || 0) !== 1 ? 's' : ''}
                      </td>
                      <td className="px-6 py-4 text-sm text-virgo-teal-400">
                        {client.total_documents || 0} document{(client.total_documents || 0) !== 1 ? 's' : ''}
                      </td>
                    </tr>
                  ))}
                  {(!organized?.clients || organized.clients.length === 0) && (
                    <tr>
                      <td colSpan="3" className="px-6 py-8 text-center text-virgo-teal-400">
                        No clients found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {/* Cases list view (within a client) */}
            {activeTab === 'cases' && selectedClient && !selectedCase && searchResults === null && (
              <table className="w-full">
                <thead className="bg-virgo-navy-600 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Case</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Documents</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {selectedClient.cases?.map((caseItem) => (
                    <tr
                      key={caseItem.id}
                      className="hover:bg-virgo-navy-600 cursor-pointer"
                      onClick={() => handleCaseSelect(caseItem)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Folder className="text-virgo-teal-300 mr-3" size={20} />
                          <div>
                            <span className="text-white font-medium">{caseItem.case_number}</span>
                            <span className="text-virgo-teal-400 ml-2">- {caseItem.case_name}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-2 py-1 rounded text-xs ${
                          caseItem.status === 'active' ? 'bg-green-900 text-green-300' :
                          caseItem.status === 'closed' ? 'bg-gray-700 text-gray-300' :
                          'bg-yellow-900 text-yellow-300'
                        }`}>
                          {caseItem.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-virgo-teal-400">
                        {caseItem.document_count} document{caseItem.document_count !== 1 ? 's' : ''}
                      </td>
                    </tr>
                  ))}
                  {(!selectedClient.cases || selectedClient.cases.length === 0) && (
                    <tr>
                      <td colSpan="3" className="px-6 py-8 text-center text-virgo-teal-400">
                        No cases for this client
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {/* Case folders view (subfolders within a case) */}
            {activeTab === 'cases' && selectedCase && !selectedFolder && searchResults === null && (
              <table className="w-full">
                <thead className="bg-virgo-navy-600 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Folder</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Contents</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {/* All Documents option - shows unfiled documents */}
                  {(caseFolderId || unfiledDocumentCount > 0) && (
                    <tr
                      className="hover:bg-virgo-navy-600 cursor-pointer bg-virgo-navy-650"
                      onClick={() => handleFolderSelect({ id: caseFolderId, name: 'All Documents', folder_type: 'case' })}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <File className="mr-3 text-virgo-gold-400" size={20} />
                          <span className="text-white font-medium">All Documents</span>
                          <span className="ml-2 text-xs text-virgo-teal-500">(unfiled)</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-virgo-gold-400">
                        {unfiledDocumentCount} document{unfiledDocumentCount !== 1 ? 's' : ''}
                      </td>
                    </tr>
                  )}
                  {caseFolders.map((folder) => (
                    <tr
                      key={folder.id}
                      className="hover:bg-virgo-navy-600 cursor-pointer"
                      onClick={() => handleFolderSelect(folder)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Folder className={`mr-3 ${
                            folder.name === 'Communications' ? 'text-blue-400' :
                            folder.name === 'Evidence' ? 'text-red-400' :
                            folder.name === 'Court Documents' ? 'text-cyan-400' :
                            'text-virgo-teal-300'
                          }`} size={20} />
                          <span className="text-white font-medium">{folder.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-virgo-teal-400">
                        {folder.document_count || 0} document{(folder.document_count || 0) !== 1 ? 's' : ''}
                        {folder.subfolder_count > 0 && `, ${folder.subfolder_count} subfolder${folder.subfolder_count !== 1 ? 's' : ''}`}
                      </td>
                    </tr>
                  ))}
                  {caseFolders.length === 0 && !caseFolderId && unfiledDocumentCount === 0 && (
                    <tr>
                      <td colSpan="2" className="px-6 py-8 text-center text-virgo-teal-400">
                        No folders found for this case
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}

            {/* Documents list view (inside a folder or for templates/personal) */}
            {(activeTab !== 'cases' || selectedFolder || searchResults !== null) && (
              <table className="w-full">
                <thead className="bg-virgo-navy-600 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Size</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Uploaded</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {/* Show subfolders first if any */}
                  {caseFolders.map((folder) => (
                    <tr
                      key={`folder-${folder.id}`}
                      className="hover:bg-virgo-navy-600 cursor-pointer"
                      onClick={() => handleFolderSelect(folder)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Folder className={`mr-3 ${
                            folder.name === 'Emails' ? 'text-blue-400' :
                            folder.name === 'Sigil' ? 'text-green-400' :
                            folder.name === 'Phone Recordings' ? 'text-orange-400' :
                            'text-virgo-teal-300'
                          }`} size={20} />
                          <span className="text-white font-medium">{folder.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-virgo-teal-400">
                        {folder.document_count || 0} item{(folder.document_count || 0) !== 1 ? 's' : ''}
                      </td>
                      <td className="px-6 py-4 text-sm text-virgo-teal-400">—</td>
                      <td className="px-6 py-4"></td>
                    </tr>
                  ))}
                  {/* Show documents */}
                  {displayDocuments.map((doc) => (
                    <tr key={doc.id} className="hover:bg-virgo-navy-600">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getFileIcon(doc.mime_type)}
                          <span className="text-white">{(doc.display_name || doc.original_file_name)}</span>
                          {/* Protection indicators */}
                          {doc.protections && doc.protections.length > 0 && (
                            <div className="ml-2">
                              <ProtectionIndicator
                                protections={doc.protections}
                                onClick={() => setProtectionsDoc(doc)}
                              />
                            </div>
                          )}
                          {/* Legacy indicators (fallback) */}
                          {!doc.protections?.length && doc.is_confidential && (
                            <Lock size={14} className="ml-2 text-red-400" title="Confidential" />
                          )}
                          {!doc.protections?.length && doc.is_privileged && (
                            <Shield size={14} className="ml-2 text-yellow-400" title="Privileged" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-virgo-teal-400">
                        {documentService.formatFileSize(doc.file_size)}
                      </td>
                      <td className="px-6 py-4 text-sm text-virgo-teal-400">
                        {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          {(doc.mime_type === 'application/pdf' || doc.mime_type?.startsWith('image/')) && (
                            <button
                              onClick={() => setPreviewDoc(doc)}
                              className="text-virgo-teal-400 hover:text-virgo-teal-200"
                              title="Preview"
                            >
                              <Eye size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => handleDownload(doc)}
                            className="text-virgo-teal-400 hover:text-virgo-teal-200"
                            title="Download"
                          >
                            <Download size={18} />
                          </button>
                          {/* Share with client - only for case documents */}
                          {activeTab === 'cases' && selectedCase && (
                            <button
                              onClick={() => setShareDoc(doc)}
                              className="text-blue-400 hover:text-blue-200"
                              title="Share with Client"
                            >
                              <Share2 size={16} />
                            </button>
                          )}
                          {/* Send for Signature - only for PDFs */}
                          {doc.mime_type === 'application/pdf' && (
                            <button
                              onClick={() => handleSendForSignature(doc)}
                              className="text-purple-400 hover:text-purple-200"
                              title="Send for Signature"
                            >
                              <PenTool size={16} />
                            </button>
                          )}
                          {/* Manage protections - only for case documents */}
                          {activeTab === 'cases' && selectedCase && (
                            <button
                              onClick={() => setProtectionsDoc(doc)}
                              className="text-amber-400 hover:text-amber-200"
                              title="Manage Protections"
                            >
                              <Shield size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => handleRename(doc)}
                            className="text-virgo-teal-400 hover:text-virgo-teal-200"
                            title="Rename"
                          >
                            <Edit2 size={16} />
                          </button>
                          {canDelete(doc) && (
                            <button
                              onClick={() => handleDelete(doc)}
                              className="text-red-400 hover:text-red-200"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {displayDocuments.length === 0 && caseFolders.length === 0 && (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center text-virgo-teal-400">
                        {searchResults !== null ? 'No documents match your search' : 'No documents found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-virgo-navy-700 rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-virgo-teal-100">Upload Document</h2>
              <button
                onClick={closeUploadModal}
                disabled={uploading}
                className="text-virgo-teal-400 hover:text-virgo-teal-200 disabled:opacity-50"
              >
                <X size={24} />
              </button>
            </div>

            <div className="mb-4 p-3 bg-virgo-navy-600 rounded-md">
              <p className="text-sm text-virgo-teal-300">
                Uploading to: <span className="text-white font-medium">
                  {activeTab === 'cases' && selectedCase
                    ? `${selectedClient?.name || 'Unknown'} / ${selectedCase.case_name}`
                    : activeTab === 'templates'
                    ? 'Templates'
                    : activeTab === 'scans'
                    ? 'Scans'
                    : 'Personal'}
                </span>
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                Select File
              </label>
              <input
                type="file"
                onChange={handleFileSelect}
                disabled={uploading}
                className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-600 rounded-md text-white disabled:opacity-50"
              />
              {selectedFile && (
                <p className="mt-2 text-sm text-virgo-teal-400">
                  Selected: {selectedFile.name} ({documentService.formatFileSize(selectedFile.size)})
                </p>
              )}
            </div>

            {uploading && (
              <div className="mb-4">
                <div className="w-full bg-virgo-navy-600 rounded-full h-2">
                  <div
                    className="bg-virgo-teal-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-virgo-teal-400 mt-2">
                  Uploading... {uploadProgress}%
                </p>
              </div>
            )}

            {uploadError && (
              <div className="mb-4 p-3 bg-red-900 bg-opacity-50 border border-red-700 rounded-md">
                <p className="text-sm text-red-300">{uploadError}</p>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={closeUploadModal}
                disabled={uploading}
                className="px-4 py-2 bg-virgo-navy-600 text-virgo-teal-300 rounded-md hover:bg-virgo-navy-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                className="px-4 py-2 bg-virgo-teal-600 text-white rounded-md hover:bg-virgo-teal-500 disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-virgo-navy-800 rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-virgo-navy-600">
              <div className="flex items-center space-x-3">
                <FileText className="text-virgo-teal-400" size={24} />
                <div>
                  <h3 className="text-lg font-semibold text-white">{(previewDoc.display_name || previewDoc.original_file_name)}</h3>
                  <p className="text-sm text-virgo-teal-400">{documentService.formatFileSize(previewDoc.file_size)}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleDownload(previewDoc)}
                  className="px-3 py-1.5 bg-virgo-teal-600 text-white rounded hover:bg-virgo-teal-500 flex items-center space-x-1"
                >
                  <Download size={16} />
                  <span>Download</span>
                </button>
                <button
                  onClick={() => setPreviewDoc(null)}
                  className="p-2 text-virgo-teal-400 hover:text-white rounded-full hover:bg-virgo-navy-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden p-4">
              {previewDoc.mime_type === 'application/pdf' ? (
                <iframe
                  src={`/api/documents/${previewDoc.id}/preview`}
                  className="w-full h-full rounded border border-virgo-navy-600"
                  title={(previewDoc.display_name || previewDoc.original_file_name)}
                />
              ) : previewDoc.mime_type?.startsWith('image/') ? (
                <div className="w-full h-full flex items-center justify-center">
                  <img
                    src={`/api/documents/${previewDoc.id}/preview`}
                    alt={(previewDoc.display_name || previewDoc.original_file_name)}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-virgo-teal-400">
                  Preview not available for this file type
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Manage Protections Modal */}
      <ManageProtectionsModal
        isOpen={!!protectionsDoc}
        onClose={() => setProtectionsDoc(null)}
        document={protectionsDoc}
        onUpdate={() => loadDocuments()}
      />

      {/* Share Document Modal */}
      <ShareDocumentModal
        isOpen={!!shareDoc}
        onClose={() => setShareDoc(null)}
        documents={shareDoc ? [shareDoc] : []}
        caseId={selectedCase?.id}
        onShare={() => loadDocuments()}
      />

      {/* Send for Signature Modal */}
      {signatureDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-virgo-navy-700 rounded-xl border border-virgo-teal-700/30 shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-virgo-navy-600 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">Send for Signature</h3>
                <p className="text-sm text-virgo-teal-400 mt-1 truncate max-w-md">{(signatureDoc.display_name || signatureDoc.original_file_name)}</p>
              </div>
              <button
                onClick={() => setSignatureDoc(null)}
                className="text-virgo-teal-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-virgo-teal-300">
                    Signers
                  </label>
                  <button
                    type="button"
                    onClick={handleAddSignatureSigner}
                    className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
                  >
                    <Plus size={12} />
                    Add Signer
                  </button>
                </div>
                <div className="space-y-3">
                  {signatureSigners.map((signer, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={signer.name}
                          onChange={(e) => handleSignatureSignerChange(index, 'name', e.target.value)}
                          placeholder="Full Name"
                          className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                        />
                      </div>
                      <div className="flex-1">
                        <input
                          type="email"
                          value={signer.email}
                          onChange={(e) => handleSignatureSignerChange(index, 'email', e.target.value)}
                          placeholder="Email"
                          className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                        />
                      </div>
                      <div className="w-24">
                        <select
                          value={signer.type}
                          onChange={(e) => handleSignatureSignerChange(index, 'type', e.target.value)}
                          className="w-full px-2 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                        >
                          <option value="client">Client</option>
                          <option value="attorney">Attorney</option>
                          <option value="witness">Witness</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      {signatureSigners.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveSignatureSigner(index)}
                          className="p-2 text-red-400 hover:text-red-300 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-virgo-navy-600 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setSignatureDoc(null)}
                className="px-4 py-2 text-sm font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitForSignature}
                disabled={sendingForSignature}
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-indigo-500 rounded-lg hover:from-purple-600 hover:to-indigo-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {sendingForSignature ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <PenTool size={16} />
                    Send for Signature
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Documents;
